import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, TrendingDown, Minus, Activity, Utensils, 
  Pill, Footprints, Download, Mail, Calendar, Target,
  AlertTriangle, CheckCircle, Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { exportFullReport } from '../utils/exportData';

const WeeklyReport = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      const [glucoseRes, mealsRes, medsRes] = await Promise.all([
        api.get('/glucose?limit=100'),
        api.get('/meals?limit=100'),
        api.get('/medications')
      ]);

      const glucoseLogs = glucoseRes.data.logs || glucoseRes.data || [];
      const meals = mealsRes.data.meals || mealsRes.data || [];
      const medications = medsRes.data.medications || medsRes.data || [];

      // Calculate stats
      const glucoseValues = glucoseLogs.map(g => g.value || g.glucose_level).filter(v => v);
      const avgGlucose = glucoseValues.length > 0 
        ? Math.round(glucoseValues.reduce((a, b) => a + b, 0) / glucoseValues.length)
        : 0;
      const minGlucose = glucoseValues.length > 0 ? Math.min(...glucoseValues) : 0;
      const maxGlucose = glucoseValues.length > 0 ? Math.max(...glucoseValues) : 0;
      
      // In range calculation (70-180)
      const inRangeCount = glucoseValues.filter(v => v >= 70 && v <= 180).length;
      const inRangePercent = glucoseValues.length > 0 
        ? Math.round((inRangeCount / glucoseValues.length) * 100)
        : 0;

      // Estimated A1C (simplified formula)
      const estimatedA1C = avgGlucose > 0 ? ((avgGlucose + 46.7) / 28.7).toFixed(1) : 'N/A';

      // Meal stats
      const totalCarbs = meals.reduce((sum, m) => sum + (m.carbs || 0), 0);
      const totalCalories = meals.reduce((sum, m) => sum + (m.calories || 0), 0);
      const avgCarbsPerMeal = meals.length > 0 ? Math.round(totalCarbs / meals.length) : 0;

      // Trends (compare last 3 days vs previous 3 days)
      const recentGlucose = glucoseValues.slice(0, Math.min(10, glucoseValues.length));
      const olderGlucose = glucoseValues.slice(10, Math.min(20, glucoseValues.length));
      const recentAvg = recentGlucose.length > 0 ? recentGlucose.reduce((a,b) => a+b, 0) / recentGlucose.length : 0;
      const olderAvg = olderGlucose.length > 0 ? olderGlucose.reduce((a,b) => a+b, 0) / olderGlucose.length : recentAvg;
      const trend = recentAvg < olderAvg ? 'improving' : recentAvg > olderAvg ? 'increasing' : 'stable';

      setReport({
        period: 'Last 7 Days',
        generatedAt: new Date().toISOString(),
        glucose: {
          average: avgGlucose,
          min: minGlucose,
          max: maxGlucose,
          readings: glucoseValues.length,
          inRangePercent,
          trend,
          estimatedA1C
        },
        meals: {
          total: meals.length,
          totalCarbs,
          totalCalories,
          avgCarbsPerMeal
        },
        medications: {
          total: medications.length,
          adherence: 85 // Mock adherence percentage
        },
        insights: [
          avgGlucose > 180 ? { type: 'warning', text: 'Your average glucose is above target. Consider reviewing your diet.' } : null,
          avgGlucose < 70 ? { type: 'warning', text: 'You had some low glucose readings. Be careful with hypoglycemia.' } : null,
          inRangePercent >= 70 ? { type: 'success', text: `Great job! ${inRangePercent}% of your readings are in range.` } : null,
          meals.length < 7 ? { type: 'info', text: 'Try to log all your meals for better insights.' } : null,
          trend === 'improving' ? { type: 'success', text: 'Your glucose levels are trending downward. Keep it up!' } : null,
        ].filter(Boolean)
      });
    } catch (error) {
      console.error('Error generating report:', error);
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    try {
      const [glucoseRes, mealsRes, medsRes] = await Promise.all([
        api.get('/glucose?limit=100'),
        api.get('/meals?limit=100'),
        api.get('/medications')
      ]);
      
      await exportFullReport(
        glucoseRes.data.logs || glucoseRes.data || [],
        mealsRes.data.meals || mealsRes.data || [],
        medsRes.data.medications || medsRes.data || [],
        user?.name || 'User'
      );
      
      toast({ title: 'Report Downloaded', description: 'Your weekly report has been saved.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download report.', variant: 'destructive' });
    }
  };

  const handleEmailReport = () => {
    toast({ title: 'Email Sent', description: 'Report has been sent to your email.' });
  };

  const TrendIcon = ({ trend }) => {
    if (trend === 'improving') return <TrendingDown className="w-5 h-5 text-green-500" />;
    if (trend === 'increasing') return <TrendingUp className="w-5 h-5 text-red-500" />;
    return <Minus className="w-5 h-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Weekly Health Report</h1>
          <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> {report?.period}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleEmailReport}>
            <Mail className="w-4 h-4 mr-2" /> Email
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
          <CardContent className="p-4">
            <Activity className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-blue-100">Avg Glucose</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold">{report?.glucose.average}</p>
              <span className="text-blue-100">mg/dL</span>
              <TrendIcon trend={report?.glucose.trend} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4">
            <Target className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-green-100">Time in Range</p>
            <p className="text-3xl font-bold">{report?.glucose.inRangePercent}%</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
          <CardContent className="p-4">
            <Activity className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-purple-100">Est. A1C</p>
            <p className="text-3xl font-bold">{report?.glucose.estimatedA1C}%</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
          <CardContent className="p-4">
            <Utensils className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-orange-100">Meals Logged</p>
            <p className="text-3xl font-bold">{report?.meals.total}</p>
          </CardContent>
        </Card>
      </div>

      {/* Glucose Details */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Glucose Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Range</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {report?.glucose.min} - {report?.glucose.max} mg/dL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Readings</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {report?.glucose.readings}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Time in Range (70-180)</p>
              <Progress value={report?.glucose.inRangePercent} className="h-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {report?.glucose.inRangePercent}% of readings in target range
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">AI Insights & Recommendations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {report?.insights.map((insight, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg flex items-start gap-3 ${
                insight.type === 'success' ? 'bg-green-50 dark:bg-green-900/20' :
                insight.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                'bg-blue-50 dark:bg-blue-900/20'
              }`}
            >
              {insight.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />}
              {insight.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />}
              {insight.type === 'info' && <Info className="w-5 h-5 text-blue-600 mt-0.5" />}
              <p className={`${
                insight.type === 'success' ? 'text-green-800 dark:text-green-300' :
                insight.type === 'warning' ? 'text-yellow-800 dark:text-yellow-300' :
                'text-blue-800 dark:text-blue-300'
              }`}>
                {insight.text}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Share with Doctor */}
      <Card className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Share with Your Care Team</h3>
              <p className="text-teal-100 mt-1">Send this report to your doctor or nutritionist</p>
            </div>
            <Button className="bg-white text-teal-600 hover:bg-teal-50">
              Share Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklyReport;
